"use client";
import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "../utils/utils";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/src/components/ui/navigation-menu";
import React from "react";

const Path = (props: any) => (
  <motion.path
    className="stroke-secondary-foreground"
    fill="transparent"
    strokeWidth="3"
    stroke="hsl(0, 0%, 18%)"
    strokeLinecap="round"
    {...props}
  />
);

const NavButton = () => {
  return (
    <svg width="23" height="23" viewBox="0 0 23 23">
      <Path
        variants={{
          closed: { d: "M 2 2 L 20 2" },
          open: { d: "M 3 16.5 L 17 2.5" },
        }}
      />
      <Path
        d="M 2 10 L 20 10"
        variants={{
          closed: { opacity: 1 },
          open: { opacity: 0 },
        }}
        transition={{ duration: 0.1 }}
      />
      <Path
        variants={{
          closed: { d: "M 2 18 L 20 18" },
          open: { d: "M 3 2.5 L 17 17" },
        }}
      />
    </svg>
  );
};

const Nav = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.nav
      initial={false}
      animate={isOpen ? "open" : "closed"}
      className="z-51"
    >
      <div className="z-52">
        <div className="grid lg:grid-cols-2 items-center">
          <Link
            to="/"
            className="text-3xl w-fit flex font-bold px-4 py-2 md:py-4 lg:py-6"
          >
            <img
              src="logo.jpg"
              alt="Wilson Wong & Associates"
              className="block md:hidden h-12 object-contain"
            />
            <span className="hidden md:block">Wilson Wong & Associates​</span>
          </Link>
          <div className="hidden lg:block text-red-500 text-lg font-semibold text-right px-8 lg:px-12 py-4 lg:py-6">
            10 Years of Professional Services
          </div>
        </div>
        <SmallNav />
      </div>
      <motion.ul
        className="[&>li]:py-2 md:[&>li]:mb-6 px-8 py-2 border-y border-slate-300 font-semibold z-53 text-secondary-foreground"
        hidden={!isOpen}
      >
        <motion.li whileHover="hover">
          <Link to="/" onClick={() => setIsOpen(false)}>
            Home
          </Link>
        </motion.li>
        <motion.li whileHover="hover">
          <Link to="/about" onClick={() => setIsOpen(false)}>
            About Us
          </Link>
        </motion.li>
        <motion.li whileHover="hover">
          <Link to="/office" onClick={() => setIsOpen(false)}>
            Office
          </Link>
        </motion.li>
        <motion.li whileHover="hover">
          <Link to="/service" onClick={() => setIsOpen(false)}>
            Services
          </Link>
        </motion.li>
        <motion.li whileHover="hover">
          <Link to="/experience" onClick={() => setIsOpen(false)}>
            Experience
          </Link>
        </motion.li>
        <motion.li whileHover="hover">
          <Link to="/career" onClick={() => setIsOpen(false)}>
            Careers
          </Link>
        </motion.li>
      </motion.ul>
      <motion.button
        className="fixed md:hidden top-[20px] right-[20.5px] z-53 p-2"
        onClick={() => setIsOpen(!isOpen)}
      >
        <NavButton />
      </motion.button>
    </motion.nav>
  );
};

const aboutComponents: { title: string; href: string; description: string }[] =
  [
    {
      title: "Company",
      href: "/about",
      description: "",
    },
    {
      title: "Office",
      href: "/office",
      description: "",
    },
  ];

const style = cn(navigationMenuTriggerStyle(), "bg-transparent!");

export function SmallNav() {
  return (
    <NavigationMenu className="hidden md:flex border-y max-w-none! bg-slate-100">
      <NavigationMenuList className="[&>*]:border-r">
        <NavigationMenuItem>
          <NavigationMenuLink href="/" className={style}>
            Home
          </NavigationMenuLink>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <NavigationMenuTrigger className="bg-transparent!">
            About
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="grid w-[400px] gap-3 p-4">
              {aboutComponents.map((component) => (
                <ListItem
                  key={component.title}
                  title={component.title}
                  href={component.href}
                >
                  {component.description}
                </ListItem>
              ))}
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <Link to="/service">
            <NavigationMenuLink className={style}>Services</NavigationMenuLink>
          </Link>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <Link to="/experience">
            <NavigationMenuLink className={style}>
              Experience
            </NavigationMenuLink>
          </Link>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <Link to="/career">
            <NavigationMenuLink className={style}>Careers</NavigationMenuLink>
          </Link>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}

const ListItem = React.forwardRef<
  React.ElementRef<"a">,
  React.ComponentPropsWithoutRef<"a">
>(({ className, title, children, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
            className
          )}
          {...props}
        >
          <div className="text-sm font-medium leading-none">{title}</div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {children}
          </p>
        </a>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = "ListItem";

export default Nav;
